<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { intlLocale } from '../i18n';

// Price line of the instrument screen (docs/instrument/SPEC.md): green when the period is up, red when down,
// crosshair with the price and time on touch or hover, min and max on the right.
const props = defineProps({
  // [[iso time, price number], …], oldest first
  points: {
    type: Array,
    required: true,
  },
  // '1d' | '1w' | '1m' | '1y' | '5y': how to label the time
  period: {
    type: String,
    required: true,
  },
  up: {
    type: Boolean,
    default: true,
  },
  formatPrice: {
    type: Function,
    required: true,
  },
  height: {
    type: Number,
    default: 220,
  },
  // Paid dividends, «D» on the line at the record date (docs/instrument/SPEC.md, decision 8): [{ date, text }]
  markers: {
    type: Array,
    default: () => [],
  },
});

// Room above the highest and below the lowest point for their price labels (decision 20).
const PAD_TOP = 20;
const PAD_BOTTOM = 18;
// Row of period labels under the line (decision 15)
const LABELS = 18;
const LABEL_GAP = 44;

const root = ref(null);
const width = ref(0);
let observer = null;

onMounted(() => {
  observer = new ResizeObserver(([entry]) => { width.value = Math.floor(entry.contentRect.width); });
  observer.observe(root.value);
});
onUnmounted(() => observer?.disconnect());

// The line takes the whole width: the prices sit on the chart itself (decision 20).
const plotWidth = computed(() => width.value);
const range = computed(() => {
  const values = props.points.map((p) => p[1]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  return { min, max, span: max - min || Math.abs(max) * 0.01 || 1 };
});

function x(index) {
  return props.points.length < 2 ? plotWidth.value : (index / (props.points.length - 1)) * plotWidth.value;
}

const plotBottom = computed(() => props.height - LABELS);

function y(value) {
  const { min, span } = range.value;
  return PAD_TOP + (1 - (value - min) / span) * (plotBottom.value - PAD_TOP - PAD_BOTTOM);
}

// Max and min on the right edge, in the margins: the max above the line's highest level, the min below
// its lowest, so they never cover the line (decision 20).
const extremes = computed(() => {
  if (props.points.length < 2 || plotWidth.value === 0) {
    return [];
  }
  const right = { x: plotWidth.value - 2, anchor: 'end' };
  return [
    { key: 'max', label: props.formatPrice(range.value.max), y: y(range.value.max) - 7, ...right },
    { key: 'min', label: props.formatPrice(range.value.min), y: y(range.value.min) + 15, ...right },
  ];
});

const line = computed(() => props.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[1]).toFixed(1)}`).join(''));
const area = computed(() => (props.points.length < 2 ? '' : `${line.value}L${plotWidth.value},${plotBottom.value}L0,${plotBottom.value}Z`));

// Period dividers (decision 15): faint dashed lines where the hour, day, week, month or year changes,
// labelled under the chart. Moscow time, as the exchange.
const UNIT = { '1d': 'hour', '1w': 'day', '1m': 'week', '1y': 'month', '5y': 'year', all: 'year' };
const MOSCOW_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23', weekday: 'short',
});
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function moscowParts(iso) {
  const parts = Object.fromEntries(MOSCOW_PARTS.formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return { year: parts.year, month: parts.month, day: parts.day, hour: parts.hour, weekday: WEEKDAYS.indexOf(parts.weekday) };
}

function unitKey(p, unit) {
  if (unit === 'hour') {
    return `${p.year}${p.month}${p.day}${p.hour}`;
  }
  if (unit === 'day') {
    return `${p.year}${p.month}${p.day}`;
  }
  if (unit === 'week') {
    // The Monday the week starts with
    const monday = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day) - p.weekday));
    return monday.toISOString().slice(0, 10);
  }
  return unit === 'month' ? `${p.year}${p.month}` : p.year;
}

function unitLabel(p, unit) {
  const date = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 12));
  if (unit === 'hour') {
    return `${p.hour}:00`;
  }
  if (unit === 'day') {
    return String(Number(p.day));
  }
  if (unit === 'week') {
    return new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
  }
  if (unit === 'month') {
    // January carries the year, the other months their names
    return p.month === '01' ? p.year : new Intl.DateTimeFormat(intlLocale(), { month: 'long', timeZone: 'UTC' }).format(date);
  }
  return p.year;
}

const dividers = computed(() => {
  const unit = UNIT[props.period] ?? 'year';
  const result = [];
  let previous = null;
  let lastX = -Infinity;
  props.points.forEach(([time], index) => {
    const parts = moscowParts(time);
    const key = unitKey(parts, unit);
    if (previous !== null && key !== previous) {
      const at = x(index);
      // Labels must not run into each other or past the price axis.
      if (at - lastX >= LABEL_GAP && at <= plotWidth.value - 16) {
        result.push({ key, x: at, label: unitLabel(parts, unit) });
        lastX = at;
      }
    }
    previous = key;
  });
  return result;
});

const color = computed(() => (props.up ? '#34d399' : '#fb7185'));
const gradientId = `instrument-chart-${Math.random().toString(36).slice(2)}`;

// A mark sits on the first point of its day or later; marks outside the period are not drawn.
const MOSCOW_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' });
const marks = computed(() => {
  if (props.points.length === 0) {
    return [];
  }
  const days = props.points.map((p) => MOSCOW_DATE.format(new Date(p[0])));
  return props.markers.flatMap((marker) => {
    if (marker.date < days[0] || marker.date > days[days.length - 1]) {
      return [];
    }
    const index = days.findIndex((day) => day >= marker.date);
    return index < 0 ? [] : [{ ...marker, index, x: x(index), y: y(props.points[index][1]) }];
  });
});

// Crosshair: nearest point to the pointer.
const active = ref(null);

function onPointer(event) {
  if (props.points.length === 0 || plotWidth.value === 0) {
    return;
  }
  const rect = root.value.getBoundingClientRect();
  const ratio = Math.min(Math.max((event.clientX - rect.left) / plotWidth.value, 0), 1);
  active.value = Math.round(ratio * (props.points.length - 1));
}

function timeLabel(iso) {
  const date = new Date(iso);
  const options = {
    '1d': { hour: '2-digit', minute: '2-digit' },
    '1w': { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
  }[props.period] ?? { day: 'numeric', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat(intlLocale(), options).format(date);
}

const tip = computed(() => {
  if (active.value === null || !props.points[active.value]) {
    return null;
  }
  const [time, price] = props.points[active.value];
  const left = x(active.value);
  // Near a «D» its dividend joins the tip (within 2 % of the width).
  const mark = marks.value.find((m) => Math.abs(m.x - left) <= Math.max(plotWidth.value * 0.02, 4));
  return { left, top: y(price), price: props.formatPrice(price), time: timeLabel(time), mark: mark?.text ?? null, alignRight: left > plotWidth.value / 2 };
});
</script>

<template>
  <div
    ref="root"
    class="relative w-full touch-pan-y select-none"
    :style="{ height: `${height}px` }"
    @pointermove="onPointer"
    @pointerdown="onPointer"
    @pointerleave="active = null"
  >
    <svg
      v-if="width > 0 && points.length > 0"
      :width="width"
      :height="height"
      class="block"
      role="img"
      :aria-label="`${formatPrice(points[0][1])} → ${formatPrice(points[points.length - 1][1])}`"
    >
      <defs>
        <linearGradient
          :id="gradientId"
          x1="0"
          x2="0"
          y1="0"
          y2="1"
        >
          <stop
            offset="0%"
            :stop-color="color"
            stop-opacity="0.22"
          />
          <stop
            offset="100%"
            :stop-color="color"
            stop-opacity="0"
          />
        </linearGradient>
      </defs>
      <g
        v-for="divider in dividers"
        :key="divider.key"
      >
        <line
          :x1="divider.x"
          :x2="divider.x"
          :y1="PAD_TOP"
          :y2="plotBottom"
          stroke="#475569"
          stroke-opacity="0.35"
          stroke-width="1"
          stroke-dasharray="2 4"
        />
        <text
          :x="divider.x + 3"
          :y="height - 4"
          class="fill-slate-500 text-[0.6875rem] tabular-nums"
        >{{ divider.label }}</text>
      </g>
      <path
        v-if="area"
        :d="area"
        :fill="`url(#${gradientId})`"
      />
      <path
        :d="line"
        fill="none"
        :stroke="color"
        stroke-width="2"
        stroke-linejoin="round"
        stroke-linecap="round"
      />
      <!-- Max and min on the right, above and below the line (decision 20) -->
      <text
        v-for="extreme in extremes"
        :key="extreme.key"
        :x="extreme.x"
        :y="extreme.y"
        :text-anchor="extreme.anchor"
        class="fill-slate-400 text-[0.6875rem] tabular-nums"
      >{{ extreme.label }}</text>
      <g
        v-for="mark in marks"
        :key="mark.date"
      >
        <title>{{ mark.text }}</title>
        <circle
          :cx="mark.x"
          :cy="mark.y"
          r="8"
          fill="#1e293b"
          stroke="#94a3b8"
          stroke-width="1"
        />
        <text
          :x="mark.x"
          :y="mark.y + 3.5"
          text-anchor="middle"
          class="fill-slate-100 text-[0.625rem] font-semibold"
        >D</text>
      </g>
      <template v-if="tip">
        <line
          :x1="tip.left"
          :x2="tip.left"
          :y1="0"
          :y2="plotBottom"
          stroke="#64748b"
          stroke-width="1"
          stroke-dasharray="3 3"
        />
        <circle
          :cx="tip.left"
          :cy="tip.top"
          r="4"
          :fill="color"
          stroke="#0f172a"
          stroke-width="2"
        />
      </template>
    </svg>
    <div
      v-if="tip"
      class="pointer-events-none absolute top-0 rounded-lg bg-slate-800 px-2 py-1 text-[0.75rem] leading-4 tabular-nums text-slate-100 shadow"
      :style="tip.alignRight ? { right: `${width - tip.left + 8}px` } : { left: `${tip.left + 8}px` }"
    >
      <span class="block font-medium">{{ tip.price }}</span>
      <span class="block text-slate-400">{{ tip.time }}</span>
      <span
        v-if="tip.mark"
        class="block text-slate-300"
      >{{ tip.mark }}</span>
    </div>
  </div>
</template>
